// One warped clip's audio at the room's tempo — the stretch engine's single
// entry point. Everything the browser plays stretched, pitched or re-pitched
// comes through here, whether it runs in the stretch Worker or, where there
// is no Worker (tests, an offline mixdown's fallback), inline. The spec
// names Live's warp mode by its value (`warpModeValue` on the wire):
//
//   Re-Pitch (3)   resample through the time map: speed follows each
//                  segment's slope and pitch goes with it (Live greys the
//                  Transpose knob out in this mode, so `semitones` is ignored)
//   Beats (0)      segments at the transients, each played as it is — in
//                  the wasm engine (stretch-rs/src/beats.rs) only
//   Complex Pro (6) the vocoder, keeping the formants under a transpose —
//                  wasm only (stretch-rs/src/fast.rs `Formant`)
//   Complex (4), Tones (1), Texture (2), and every mode in THIS file:
//                  vocode through the time map, keeping pitch; a transpose
//                  vocodes to a length 2^(st/12) longer, then resamples that
//                  back down, so the length holds and the pitch moves
//
// This TypeScript engine is the fallback (no wasm) and the inline renderer:
// it plays Beats and Complex Pro as Complex. Tones and Texture are not built
// anywhere; they are Complex in both engines.

import type { PcmSource } from "@web-shared/lib/wav";
import { fileSecondsAt, beatAtFileSeconds, WARP_MODE_REPITCH, type StretchedPcm, type WarpMarker } from "@web-shared/lib/warp";
import { vocode } from "@web-shared/lib/stretch/vocoder";
import { resampleBy } from "@web-shared/lib/stretch/resample";
import type { Onset } from "@web-shared/lib/stretch/onsets";
import { type WarpSettingsData } from "@shared/sync/protocol.js";

/** How to play a file onto the room's grid. */
export type TimeMap =
  /** A constant speed factor (room ÷ source tempo): 2 halves the length. */
  | { kind: "ratio"; ratio: number }
  /** Through warp markers onto a grid at `bpm`; `fallbackBpm` carries past the ends. */
  | { kind: "markers"; markers: readonly WarpMarker[]; bpm: number; fallbackBpm: number };

/** Live's warp modes by value: Beats, Tones, Texture, Re-Pitch, Complex,
 * Complex Pro (5 is REX, which the wire never carries). */
export type WarpModeValue = 0 | 1 | 2 | 3 | 4 | 6;
export const WARP_MODE_COMPLEX: WarpModeValue = 4;

/** `v` as a warp mode; anything else (REX, a newer mode) plays as Complex. */
export function warpModeOf(v: number | undefined): WarpModeValue {
  return v === 0 || v === 1 || v === 2 || v === 3 || v === 6 ? v : WARP_MODE_COMPLEX;
}

export interface RenderSpec {
  map: TimeMap;
  /** Live's warp mode (see the header); Re-Pitch is the one without a vocoder. */
  mode: WarpModeValue;
  /** Transpose + detune, in semitones. Ignored under Re-Pitch. */
  semitones: number;
  /** The mode's knobs (Beats: Preserve / Loop Mode / Envelope; Complex Pro:
   * Formants / Envelope); Live's defaults when absent. The wasm engine
   * reads them; this file's vocoder has nothing they apply to. */
  settings?: WarpSettingsData;
  /** The tempo Beats' Preserve divisions are measured on. */
  gridBpm?: number;
  /** The file's transients for the vocoder (`detectOnsets`); detected when absent. */
  onsets?: readonly Onset[];
}

/** Output length and the output-sample → file-sample map for `map`. */
export function timeMapOf(source: PcmSource, map: TimeMap): { outLen: number; mapIn: (o: number) => number } {
  const sr = source.sampleRate;
  if (map.kind === "ratio") {
    const ratio = map.ratio > 0 ? map.ratio : 1;
    return { outLen: Math.max(1, Math.round(source.length / ratio)), mapIn: (o) => o * ratio };
  }
  const { markers, bpm, fallbackBpm } = map;
  const endBeat = beatAtFileSeconds(markers, source.length / sr, fallbackBpm);
  const outLen = Math.max(1, Math.round(((endBeat * 60) / bpm) * sr));
  return { outLen, mapIn: (o) => fileSecondsAt(markers, ((o / sr) * bpm) / 60, fallbackBpm) * sr };
}

/** Whether rendering `spec` would change nothing (plays as the file is). */
export function isIdentity(spec: RenderSpec): boolean {
  const pitched = spec.mode !== WARP_MODE_REPITCH && Math.abs(spec.semitones) > 1e-6;
  return !pitched && !gatesAtOwnTempo(spec.mode, spec.settings) && spec.map.kind === "ratio" && Math.abs(spec.map.ratio - 1) < 1e-6;
}

/** Beats with its Transient Envelope below 100 changes the sound at the
 * file's own tempo too — every slice fades out (the drum gate Live users
 * reach for at the original BPM). */
export function gatesAtOwnTempo(mode: number, settings: WarpSettingsData | undefined): boolean {
  return mode === 0 && settings?.envelope !== undefined && settings.envelope < 100;
}

/** Render `source` per `spec`. */
export function renderWarped(source: PcmSource, spec: RenderSpec): StretchedPcm {
  const { outLen, mapIn } = timeMapOf(source, spec.map);
  if (spec.mode === WARP_MODE_REPITCH) return resampleBy(source, outLen, mapIn);
  const factor = 2 ** (spec.semitones / 12);
  const opts = spec.onsets ? { onsets: spec.onsets } : {};
  if (Math.abs(factor - 1) < 1e-6) return vocode(source, outLen, mapIn, opts);
  // Vocode to `factor`× the length (same content, same pitch), then read it
  // back `factor`× faster: the length lands on `outLen`, the pitch moves.
  const longLen = Math.max(1, Math.round(outLen * factor));
  const long = vocode(source, longLen, (o) => mapIn(o / factor), opts);
  return resampleBy(long, outLen, (o) => o * factor);
}
